import React, { useState } from 'react';
import { addCommas, getCompanyLogo, getCompanyName, getProductType } from "../utils";
import arrowDown from './arrow-down.svg';
import arrowLeft from './arrow-left.svg';
import arrow from './arrow.svg';
import './mainContent.scss';
import noChange from './no-change.svg';

interface MainContentInsuranceProps {
    data: any;    
    state: any;    
}

const MainContentInsurance: React.FC<MainContentInsuranceProps> = ({
    data,    
    state,
}) => {
    // State to track which products have expanded details
    const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());
    
    // State to track if products with noChange recommendation should be shown
    const [showNoChangeProducts, setShowNoChangeProducts] = useState<boolean>(false);

    // Function to toggle details visibility for a specific product
    const toggleProductDetails = (productIndex: number) => {
        setExpandedProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productIndex)) {
                newSet.delete(productIndex);
            } else {
                newSet.add(productIndex);
            }
            return newSet;
        });
    };    

    const getTitle = (product: any) => {
        const recommendation = product.recommendation;
        
        return `${getProductType(product.oldProduct)}`;// (${recommendation})`;
    }

    const getLogoClass = (product: any) => {        
        let logoClass = (!product.newProduct || product.oldProduct.companyShortName === product.newProduct.companyShortName) ? 'logo' : 'logo old';
        if (getCompanyLogo(product.oldProduct) && !["noChange", "agentAssignment", "new"].includes(product.recommendation)) {
            logoClass += ' greyscale';
        }
        return logoClass;
    }

    // Sort products by category only (keep same type together regardless of active/inactive status)
    const sortedProducts = state?.products ? [...state.products].sort((a: any, b: any) => {
        // Define product type priority order for insurance products
        const productTypePriority: { [key: string]: number } = {
            'lifeInsurance': 1,
            'healthInsurance': 2,
            'longTermCare': 3,
            'disabilityInsurance': 4,
            'nursingInsurance': 5,
            'criticalIllnessInsurance': 6,
            'elementaryInsurance': 7,
            'propertyInsurance': 8
        };
        
        // Get product types (use oldProduct as it always exists)
        const aType = a.oldProduct.productType;
        const bType = b.oldProduct.productType;
        
        // Get priority for each product (default to 999 if not found)
        const aPriority = productTypePriority[aType] || 999;
        const bPriority = productTypePriority[bType] || 999;
        
        // Sort by product type category only
        return aPriority - bPriority;
    }) : [];
    
    // Filter out products with noChange recommendation if the checkbox is unchecked
    const filteredProducts = showNoChangeProducts 
        ? sortedProducts 
        : sortedProducts.filter((product: any) => product.recommendation !== "noChange");
    
    return (
        <div className="content-v2">
            <div className="hi-user">היי {data?.clientName}!</div>
            <div className="texts">
                <div className="text1">
                    בהמשך לשיחתנו, מצאנו עבורך שיפור משמעותי בביטוחים!
                </div>
                <div className="text2">
                    {data?.gender === 'female' ? 'לחצי' : 'לחץ'} על הכפתור מטה כדי לאשר את ביצוע הפעולה ולחתום על הטפסים הנדרשים
                </div>
                <div className="text3">
                    הנה פירוט מוצרי הביטוח שלך, והפעולות שנבצע עבורך{" "}
                </div>
            </div>
            
            <div>
                <div className="saving-details">
                    {filteredProducts.map((product: any, index: number) => {
                        // Check if this is the first item in its product type group
                        const isFirstInGroup = index === 0 || 
                            filteredProducts[index - 1].oldProduct.productType !== product.oldProduct.productType;
                        
                        // Check if this is the last item in its product type group
                        const isLastInGroup = index === filteredProducts.length - 1 || 
                            filteredProducts[index + 1].oldProduct.productType !== product.oldProduct.productType;
                        
                        return (
                        <div key={index} className="product-item">
                            {isFirstInGroup && <div className="title">{getTitle(product)}</div>}
                            <div className="content">
                                <div className="main-line">
                                    <span className={getLogoClass(product)}>
                                        <span className="logo-img">{getCompanyLogo(product.oldProduct)?<img src={getCompanyLogo(product.oldProduct)} alt={getCompanyName(product.oldProduct)} />:<span>{getCompanyName(product.oldProduct)}</span>}</span>
                                    </span>
                                    <span className="arrow"><img src={arrow} alt="arrow" /></span>
                                    
                                    {(product.recommendation === "noChange") ? <span className="logo status-no-change">
                                        <img src={noChange} alt="ללא שינוי" />
                                        ללא שינוי
                                        </span>:
                                    (product.recommendation === "new") ? <span className="logo status-new">
                                        <span className="status-icon">+</span>
                                        מוצר חדש
                                        </span>:
                                    (product.recommendation === "partialCancellation") ? <span className="logo status-partial-cancel">
                                        <span className="status-icon">⚠</span>
                                        ביטול חלקי
                                        </span>:
                                        (product.recommendation === "fullCancellation") ? <span className="logo status-full-cancel">
                                        <span className="status-icon">✕</span>
                                        ביטול הפוליסה
                                        </span>:
                                    (product.recommendation === "agentAssignment")?
                                    <span className="logo status-agent-assignment">
                                        <span className="status-icon">★</span>
                                        מינוי סוכן
                                        </span>: 
                                    getCompanyLogo(product.newProduct)?
                                        <span className="logo logo-img"><img src={getCompanyLogo(product.newProduct)} alt={getCompanyName(product.newProduct)} /></span>
                                        :
                                        <span className={getLogoClass(product)}>{getCompanyName(product.newProduct)}</span>
                                    }
                                    <span className="arrow-down-wrapper">
                                        <span 
                                            className={`arrow-down ${expandedProducts.has(index) ? 'expanded' : ''}`}
                                            onClick={() => toggleProductDetails(index)}
                                        >
                                            <img src={arrowDown} alt="arrow-down" />
                                        </span>
                                    </span>
                                </div>
                                <div className={`details ${expandedProducts.has(index) ? 'expanded' : 'collapsed'}`}>
                                    <div className="detail-item">
                                            <div className="title">מבוטחים</div>
                                            {(product.oldProduct.insuredPersons || []).map((person: string) => (
                                                <div className="item-line">
                                                    <span className="item-text">{person}</span>
                                                    
                                                </div>
                                            ))}
                                        </div>
                                    <div className="detail-item">                                        
                                        <div className="item-line">
                                            <span className="title">עלות חודשית:</span>
                                            <span className="percentages">
                                                
                                                <span className="percentage textAlignRight">₪{addCommas(product.oldProduct.premium)}</span>
                                                {!["new","replace","agentAppointed","noChange"].includes(product.recommendation) && product.newProduct?.premium && <>
                                                <span className="left-arrow"><img src={arrowLeft} alt="arrow" /></span>                                                
                                                <span className="percentage textAlignLeft"><b>₪{addCommas(product.newProduct?.premium)}</b></span>
                                                </>}
                                            </span>
                                        </div>
                                    </div>

                                    {product.potentialSavings > 0 && (
                                        <div className="detail-item">
                                            <div className="item-line">
                                                <span className="item-text">חיסכון חודשי:</span>
                                                <span className="percentages">
                                                    <span className="percentage textAlignLeft"><b>{addCommas(product.potentialSavings)}₪</b></span>
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {product.recommendation === "partialCancellation" ? <>
                                        <div className="detail-item">
                                            <div className="title">כיסויים מבוטלים</div>
                                            {(product.oldProduct.cancelledAppendices || []).map((appendix: string) => (
                                                <div className="item-line">
                                                    <span className="item-text">{appendix}</span>
                                                    
                                                </div>
                                            ))}
                                        </div>
                                        <div className="detail-item">
                                            <div className="title">כיסויים נותרים</div>
                                            {(product.oldProduct.remainingAppendices || []).map((appendix: string) => (
                                                <div className="item-line">
                                                    <span className="item-text">{appendix}</span>
                                                    
                                                </div>
                                            ))}
                                        </div>
                                        </>:
                                        <div className="detail-item">
                                            <div className="title">כיסויים בפוליסה</div>
                                            {(product.oldProduct?.appendices || product.oldProduct?.appendices).map((appendix: string) => (
                                                <div className="item-line">
                                                    <span className="item-text">{appendix}</span>
                                                    
                                                </div>
                                            ))}
                                        </div>
                                    }
                                    
                                </div>
                                {isLastInGroup ? (
                                    <div className="separator"></div>
                                ) : (
                                    <div className="item-spacer"></div>
                                )}
                            </div>
                        </div>
                        );
                    })}
                    <div className="filter-checkbox-container" style={{ marginTop: '20px', textAlign: 'right' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', fontSize: '16px' }}>
                            <input 
                                type="checkbox" 
                                checked={showNoChangeProducts}
                                onChange={(e) => setShowNoChangeProducts(e.target.checked)}
                                style={{ marginLeft: '8px', cursor: 'pointer' }}
                            />
                            הצג מוצרים ללא שינוי
                        </label>
                    </div>
                </div>
                
                
            </div>
        </div>
    );
};

export default MainContentInsurance;

